import { authUrls, storeUrls } from '@/api/helper';
import type {
  GetMyDataResponseDto,
  GetMyPermissionsResponseDto,
  GetStoreResponseDto,
  LoginStaffPayloadDto,
  LoginStaffResponseDto,
  QueryAPI,
  RefreshTokenPayloadDto,
  RefreshTokenResponseDto,
  SendResetOTPPayloadDto,
  SignupStaffPayloadDto,
  SignupStaffResponseDto,
  UpdateSecretPayloadDto,
} from '@/types/request.dto';

const login = async (
  payload: LoginStaffPayloadDto,
  queryApi: QueryAPI,
): Promise<LoginStaffResponseDto> => {
  const abortCrt = new AbortController();
  const signal = queryApi?.signal ?? abortCrt.signal;

  const headers = new Headers();
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let data: LoginStaffResponseDto;

  try {
    const response = await fetch(authUrls.loginStaff(), {
      method: 'POST',
      body: JSON.stringify(payload),
      signal: signal,
      headers: headers,
    });
    data = await response.json();
  } catch (error) {
    throw new Error('Identifiant ou mot de passe incorrect');
  }

  return data;
};

const refresh = async (
  payload: RefreshTokenPayloadDto,
  queryApi: QueryAPI,
): Promise<RefreshTokenResponseDto> => {
  const abortCrt = new AbortController();
  const signal = queryApi?.signal ?? abortCrt.signal;

  const headers = new Headers();
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Authorization'))
    headers.set('Authorization', `Bearer ${payload.refreshToken}`);

  let data: RefreshTokenResponseDto;
  try {
    const response = await fetch(authUrls.refresh(), {
      method: 'GET',
      signal: signal,
      headers: headers,
    });
    data = await response.json();
  } catch (error) {
    throw new Error('Votre session a expiré, veuillez vous reconnecter');
  }

  return data;
};

const signup = async (payload: SignupStaffPayloadDto, queryApi: QueryAPI) => {
  const abortCrt = new AbortController();
  const signal = queryApi?.signal ?? abortCrt.signal;

  const headers = new Headers();
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let data: SignupStaffResponseDto;
  try {
    const response = await fetch(authUrls.signupStaff(), {
      method: 'POST',
      signal: signal,
      headers: headers,
      body: JSON.stringify(payload),
    });
    data = await response.json();
  } catch (error) {
    throw new Error('Cet email a déjà été utilisé');
  }

  return data;
};

const getMyData = async (
  _: void,
  queryApi: QueryAPI,
): Promise<GetMyDataResponseDto> => {
  const abortCrt = new AbortController();
  const signal = queryApi?.signal ?? abortCrt.signal;

  const headers = new Headers();
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('authorization') && queryApi.jwttoken)
    headers.set('authorization', `Bearer ${queryApi.jwttoken}`);

  let data: GetMyDataResponseDto;
  try {
    const response = await fetch(authUrls.getMe(), {
      method: 'GET',
      signal: signal,
      headers: headers,
    });
    data = await response.json();
  } catch (error) {
    throw new Error('Veuillez vous reconnecter');
  }

  return data;
};

const sendResetOTP = async (
  payload: SendResetOTPPayloadDto,
  queryApi: QueryAPI,
): Promise<boolean> => {
  const abortCrt = new AbortController();
  const signal = queryApi?.signal ?? abortCrt.signal;

  const headers = new Headers();
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let data: { message: string };
  try {
    const response = await fetch(authUrls.requestOTP(), {
      method: 'POST',
      signal: signal,
      headers: headers,
      body: JSON.stringify(payload),
    });
    data = await response.json();
  } catch (error) {
    throw new Error('Veuillez vous reconnecter');
  }

  if (!/^OTP_SEND:/i.test(data?.message))
    throw new Error('E-mail non enregistré');

  return true;
};

const updateSecret = async (
  payload: UpdateSecretPayloadDto,
  queryApi: QueryAPI,
): Promise<GetMyDataResponseDto> => {
  const abortCrt = new AbortController();
  const signal = queryApi?.signal ?? abortCrt.signal;

  const headers = new Headers();
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('authorization') && queryApi.jwttoken)
    headers.set('authorization', `Bearer ${queryApi.jwttoken}`);

  let data: GetMyDataResponseDto;
  try {
    const response = await fetch(authUrls.updateSecret(), {
      method: 'PUT',
      signal: signal,
      headers: headers,
      body: JSON.stringify(payload),
    });
    data = await response.json();
  } catch (error) {
    throw new Error('Veuillez vous reconnecter');
  }

  return data;
};

const getMyPermissions = async (
  _: void,
  queryApi: QueryAPI,
): Promise<GetMyPermissionsResponseDto> => {
  const abortCrt = new AbortController();
  const signal = queryApi?.signal ?? abortCrt.signal;

  const headers = new Headers();
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('authorization') && queryApi.jwttoken)
    headers.set('authorization', `Bearer ${queryApi.jwttoken}`);

  let data: GetMyPermissionsResponseDto;
  try {
    const response = await fetch(authUrls.getMyPermissions(), {
      method: 'GET',
      signal: signal,
      headers: headers,
    });
    data = await response.json();
  } catch (error) {
    throw new Error('Veuillez vous reconnecter');
  }

  return data;
};

const getStore = async (
  storeId: string,
  queryApi: QueryAPI,
): Promise<GetStoreResponseDto> => {
  const headers = new Headers();
  if (queryApi.jwttoken)
    headers.set('authorization', `Bearer ${queryApi.jwttoken}`);

  const response = await fetch(storeUrls.getStore(storeId), {
    method: 'GET',
    signal: queryApi.signal,
    headers: headers,
  });
  if (!response.ok) throw new Error('Commerce introuvable');
  return response.json();
};

export const authService = {
  login,
  refresh,
  signup,
  getMyData,
  getStore,
  getMyPermissions,
  sendResetOTP,
  updateSecret,
};
